/**
 * A console request's fields as they are kept (§5.11): the methods, the caps a
 * collection holds a field to, and the readers that turn untrusted JSON into a
 * header row or a form part. Shared, because a project an organization shares
 * carries its calls (§5.25) and the account area holds them to the same caps an
 * import does.
 */
import type { FormPart, HttpHeader, HttpMethod, RequestScripts } from './types';

export const HTTP_METHODS: HttpMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];

export function normalizeMethod(raw: string): HttpMethod {
  const upper = raw.trim().toUpperCase();
  return (HTTP_METHODS as string[]).includes(upper) ? (upper as HttpMethod) : 'GET';
}

/**
 * How many levels of folder the collection has. The limit is the pane's, not
 * the data's: a row is indented per level and there is only so much of a 216 px
 * column to give away. Three is deep enough to group a service, an area of it
 * and a case, and shallow enough that the first level can stay open (§5.11).
 */
export const MAX_FOLDER_DEPTH = 3;

export const MAX_COLLECTION_ROWS = 1000;
/** How long a body, and any other field of a call, may be. */
export const MAX_BODY_CHARS = 512 * 1024;
export const MAX_FIELD_CHARS = 4096;
/** How long one of a call's scripts may be — a page of JavaScript, not a library (§5.11.1). */
export const MAX_SCRIPT_CHARS = 64_000;

/** A call's scripts with both halves present — what every reader works from (§5.11.1). */
export function scriptsOf(request: { scripts?: RequestScripts }): RequestScripts {
  return { pre: request.scripts?.pre ?? '', post: request.scripts?.post ?? '' };
}

/** Whether there is a script to run at all. Blank is none: a script of whitespace does nothing. */
export function hasScripts(request: { scripts?: RequestScripts }): boolean {
  const { pre, post } = scriptsOf(request);
  return !!pre.trim() || !!post.trim();
}

/**
 * Untrusted JSON as a call's scripts, or `undefined` for none — a file, a
 * document, an agent's answer. Line endings are made one kind, so a script
 * written on Windows and one written here compare equal.
 */
export function readScripts(value: unknown): RequestScripts | undefined {
  if (!isRecord(value)) return undefined;
  const one = (raw: unknown): string => capped(raw, MAX_SCRIPT_CHARS).replace(/\r\n?/g, '\n');
  const scripts = { pre: one(value.pre), post: one(value.post) };
  return hasScripts({ scripts }) ? scripts : undefined;
}

export function readHeaders(value: unknown): HttpHeader[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((raw) =>
    isRecord(raw) && typeof raw.name === 'string'
      ? [
          {
            name: capped(raw.name, MAX_FIELD_CHARS),
            value: capped(raw.value, MAX_FIELD_CHARS),
            enabled: raw.enabled !== false
          }
        ]
      : []
  );
}

export function readForm(value: unknown): FormPart[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((raw) => {
    if (!isRecord(raw) || typeof raw.name !== 'string') return [];
    const kind = raw.kind === 'file' ? 'file' : 'text';
    const file = isRecord(raw.file) ? raw.file : null;
    return [
      {
        name: capped(raw.name, MAX_FIELD_CHARS),
        kind,
        value: capped(raw.value, MAX_BODY_CHARS),
        // A file part comes back as a name and nothing else — the bytes were
        // never in the file, so the console asks for them again (§5.11).
        file:
          kind === 'file' && file
            ? { name: capped(file.name, MAX_FIELD_CHARS), type: capped(file.type, 200), size: 0, data: '' }
            : null,
        enabled: raw.enabled !== false
      }
    ];
  });
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function capped(value: unknown, max: number): string {
  return typeof value === 'string' ? value.slice(0, max) : '';
}
